// The conversation header's plugin actions, at the Mac's placements (MacConversationView.header):
// the Google Meet button right after the call button — ONE tap, no dialog: the plugin owns the
// title and posts the meeting card itself — and «Registro», the registry's check-in (clock with a
// check), whose answer is the ephemeral card the plugin sends back over the socket. «Registro» is
// ONE click in the header on every width, every conversation (user, 2026-10-17: it was inside the
// «Complementos» menu, two steps). At phone width Meet moves INTO a "…" menu — ChannelView.swift's
// compact header; with nothing left for it, there is no menu at all.
//
// A request that never left must say so (the Mac's callNoticeText strip): otherwise a press that
// produced nothing reads exactly like a dead button. Success needs no UI — the card IS the answer.
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { data } from '../../../bridge/client';
import { AppPlugins, MEET_PLUGIN_ID, REGISTRY_PLUGIN_ID } from '../../../core/appPlugins';
import { Icon } from '../../../design/icons';
import { t } from '../../../i18n/i18n';
import { useLive } from '../../hooks';
import { useShell } from '../../shell/ShellContext';
import { useAttendance } from '../attendance/attendance';
import './appPlugins.css';

const COMPACT = '(max-width: 760px)';

function useCompact(): boolean {
  const [compact, setCompact] = useState(() => window.matchMedia(COMPACT).matches);
  useEffect(() => {
    const mq = window.matchMedia(COMPACT);
    const on = () => setCompact(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return compact;
}

/** The plugins the server runs (null = not known yet → offer everything, like the apps). */
function useActivePlugins(): string[] | null {
  return useLive(() => data.call('activeAppPlugins'), ['syncState'], []).value ?? null;
}

/** The registry's silent presence ping, sent when a conversation opens (ChannelViewModel.swift). */
export function useRegistryPresence(channelId: string): void {
  useEffect(() => {
    void data.call('registryPresence', channelId);
  }, [channelId]);
}

export interface PluginNotice {
  channelId: string;
  text: string;
}

export function AppPluginActions({ channelId, channelName, onNotice, noMeet = false }: { channelId: string; channelName: string; onNotice(notice: PluginNotice | null): void; /** An archived channel: Meet posts a message, so it is not offered; «Registro» stays (ephemeral answer, PLUGINS.md §9.5). */ noMeet?: boolean }) {
  const active = useActivePlugins();
  const compact = useCompact();
  const [menuOpen, setMenuOpen] = useState(false);
  const opener = useRef<HTMLButtonElement>(null);
  const meet = !noMeet && AppPlugins.isOffered(active, MEET_PLUGIN_ID);
  const shell = useShell();
  // «Registro» opens the native card when Core knows this person today (shared/API.md §43);
  // otherwise it stays the plugin's request (§43.4).
  const attendanceMe = useAttendance().feed?.me ?? null;
  const registry = !!attendanceMe || AppPlugins.isOffered(active, REGISTRY_PLUGIN_ID);

  useEffect(() => setMenuOpen(false), [channelId]);

  const startMeeting = useCallback(() => {
    setMenuOpen(false);
    onNotice(null);
    void data.call('startMeeting', channelId, channelName).then((ok) => {
      if (!ok) onNotice({ channelId, text: t('channel.meet.failed') });
    });
  }, [channelId, channelName, onNotice]);

  const checkIn = useCallback(() => {
    setMenuOpen(false);
    onNotice(null);
    if (attendanceMe) {
      shell.openDialog({ kind: 'attendance', view: 'card' });
      return;
    }
    void data.call('registryCheckIn', channelId).then((ok) => {
      if (!ok) onNotice({ channelId, text: t('plugins.registry.failed') });
    });
  }, [channelId, onNotice, attendanceMe, shell]);

  const meetInMenu = meet && compact;
  const hasMenu = meetInMenu;
  const menuLabel = t('web.nav.moreActions');
  return (
    <>
      {meet && !compact ? (
        <button className="icon-button" onClick={startMeeting} title={t('channel.meet.start')} aria-label={t('channel.meet.start')} data-testid="meet-start">
          <Icon name="video" size={17} />
        </button>
      ) : null}
      {registry ? (
        <button className="icon-button" onClick={checkIn} title={t('plugins.registry.checkIn')} aria-label={t('plugins.registry.checkIn')} data-testid="registry-check-in">
          <Icon name="clockCheck" size={17} />
        </button>
      ) : null}
      {hasMenu ? (
        <button
          ref={opener}
          className="icon-button"
          onClick={() => setMenuOpen((o) => !o)}
          title={menuLabel}
          aria-label={menuLabel}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-pressed={menuOpen}
          data-testid="plugins-menu"
        >
          <Icon name="more" size={17} />
        </button>
      ) : null}
      {menuOpen && opener.current ? (
        <PluginsMenu anchor={opener.current} onClose={() => setMenuOpen(false)}>
          {meetInMenu ? (
            <button type="button" className="menu-item" role="menuitem" onClick={startMeeting}>
              <Icon name="video" size={16} />
              {t('channel.meet.start')}
            </button>
          ) : null}
        </PluginsMenu>
      ) : null}
    </>
  );
}

function PluginsMenu({ anchor, onClose, children }: { anchor: HTMLElement; onClose(): void; children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const a = anchor.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    // Opens DOWN from the header, right-aligned with its button (the header sits at the top).
    setPos({ left: Math.max(8, Math.min(a.right - r.width, window.innerWidth - r.width - 8)), top: a.bottom + 6 });
  }, [anchor]);
  useEffect(() => {
    // The opener is spared: its own click toggles the menu, and closing here first would reopen it.
    const close = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && !anchor.contains(e.target as Node) && onClose();
    const esc = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onClose();
    };
    window.addEventListener('mousedown', close);
    window.addEventListener('keydown', esc, true);
    window.addEventListener('blur', onClose);
    return () => {
      window.removeEventListener('mousedown', close);
      window.removeEventListener('keydown', esc, true);
      window.removeEventListener('blur', onClose);
    };
  }, [anchor, onClose]);
  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('.menu-item')?.focus();
  }, []);
  return createPortal(
    <div
      ref={ref}
      className="popover plugins-menu"
      role="menu"
      style={pos ? { left: pos.left, top: pos.top } : { left: -9999, top: -9999 }}
      onKeyDown={(e) => {
        if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
        e.preventDefault();
        const items = [...(ref.current?.querySelectorAll<HTMLElement>('.menu-item') ?? [])];
        const i = items.indexOf(document.activeElement as HTMLElement);
        items[(i + (e.key === 'ArrowDown' ? 1 : items.length - 1)) % items.length]?.focus();
      }}
    >
      {children}
    </div>,
    document.body,
  );
}

/** The Mac's notice strip under the header: the words, and a close button. */
export function PluginNoticeStrip({ text, onClose }: { text: string; onClose(): void }) {
  return (
    <div className="call-notice-strip plugin-notice-strip" role="alert" data-testid="plugin-notice">
      <span>{text}</span>
      <button type="button" className="icon-button" onClick={onClose} aria-label={t('common.close')} title={t('common.close')}>
        <Icon name="x" size={13} strokeWidth={2.2} />
      </button>
    </div>
  );
}
